import { Button, Input, Select, Space } from 'antd';
import { DetailField, DetailFields } from 'components/DetailFields';
import Modal from 'components/Modal';
import Page from 'components/Page';
import Table from 'components/Table';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useEffect, useMemo, useState } from 'react';
import { useAccessibilityReview } from './context';
import {
  ACCEPT_ACCESSIBILITY_REVIEW_COVERAGE,
  RECORD_ACCESSIBILITY_MANUAL_REVIEW,
  UPDATE_ACCESSIBILITY_REVIEW_UNIT_STATUS,
} from './graphql';
import { InactiveNotice, PageIntroduction, PageTypeFilter, ReviewPage, REVIEW_CLASS, TABLE_SCROLL } from './ReviewPage';
import { controlStatus, reviewUnitStatus } from './tags';
import { accessibilityFindingGroups, comparableUrl } from './utils';
import { ExternalUrl } from './Evidence';
import { WcagCriterionHelp } from './WcagCriterionHelp';
import {
  controlsForPageType,
  effectiveManualReviewPageType,
  filterReviewUnits,
  pageTypeLabel,
  representativePageType,
  representativeUrl,
  reviewHasBothPageTypes,
  reviewUnitSummary,
} from './viewModel';

const applicableTargetUrls = (unit, control) => (unit?.urls || [])
  .filter(({ representative }) => representative)
  .filter((url) => {
    const applicable = control?.applicablePageTypes;
    if (!applicable) return true;
    if (!url.pageType) return true;
    return applicable.includes(url.pageType);
  });

const reviewUnitCellStyle = {
  display: 'block',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const representativeTargetStyle = {
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const MANUAL_REVIEW_TABLE_SCROLL = { x: 884 };

const targetOptionLabel = (url) => {
  const type = pageTypeLabel(url.pageType);
  const typePrefix = type === '—' ? '' : `${type} — `;
  const representativePrefix = url.representative ? 'Representative — ' : '';
  return `${representativePrefix}${typePrefix}${url.url}`;
};

export const ManualReviewModal = ({ control, reviewUnit: selectedReviewUnit, onClose, onSaved }) => {
  const { result, refetch } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const [recordManualReview, { loading: recordingManualReview }] = useMutation(RECORD_ACCESSIBILITY_MANUAL_REVIEW, {
    onCompleted: ({ recordDataSourceAccessibilityManualReview: outcome }) => {
      onClose();
      onSaved?.(outcome);
      refetch();
    },
  });
  const reviewUnits = result?.reviewUnits?.filter(({ status }) => status === 'accepted') || [];
  const [reviewUnitId, setReviewUnitId] = useState(null);
  const [pageUrl, setPageUrl] = useState(null);
  const [status, setStatus] = useState('manual_pass');
  const [rationale, setRationale] = useState('');
  const reviewUnit = reviewUnits.find(({ id }) => id === reviewUnitId)
    || reviewUnits.find(({ id }) => id === selectedReviewUnit?.id)
    || reviewUnits[0];
  const targetUrls = applicableTargetUrls(reviewUnit, control);
  const selectedUrl = targetUrls.find(({ normalizedUrl }) => normalizedUrl === pageUrl)
    || targetUrls[0]
    || null;

  useEffect(() => {
    const firstUnit = reviewUnits.find(({ id }) => id === selectedReviewUnit?.id) || reviewUnits[0];
    const firstUrl = applicableTargetUrls(firstUnit, control)[0];
    setReviewUnitId(firstUnit?.id || null);
    setPageUrl(firstUrl?.normalizedUrl || null);
    setStatus('manual_pass');
    setRationale('');
  }, [control?.id, selectedReviewUnit?.id]);

  const onUnitChange = (id) => {
    const unit = reviewUnits.find(({ id: candidateId }) => candidateId === id);
    const url = applicableTargetUrls(unit, control)[0];
    setReviewUnitId(id);
    setPageUrl(url?.normalizedUrl || null);
  };

  return (
    <Modal
      title={control ? `${reviewUnits.length ? 'Manual review' : 'Review guidance'}: WCAG ${control.id}` : ''}
      open={Boolean(control)}
      onCancel={onClose}
      footer={reviewUnits.length ? undefined : null}
      onOk={() => recordManualReview({
        variables: {
          dataSourceId: dataSource.id,
          reviewUnitId: reviewUnit?.id,
          criterionId: control.id,
          pageUrl: selectedUrl?.normalizedUrl,
          status,
          rationale,
        },
      })}
      okButtonProps={{ disabled: !reviewUnit || !selectedUrl || !rationale.trim(), loading: recordingManualReview }}
      okText="Save review"
      fullWidth={760}
      rootClassName={REVIEW_CLASS}
    >
      {control && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PageIntroduction title={control.title}>
            <WcagCriterionHelp criterion={control} />
          </PageIntroduction>
          <DetailFields>
            <DetailField label="Applies when">{control.appliesWhen}</DetailField>
          </DetailFields>
          {!reviewUnits.length && (
            <div>
              Accept the suggested coverage before saving a manual review outcome.
            </div>
          )}
          {reviewUnits.length > 0 && (
          <>
            <div>
              <label htmlFor="accessibility-review-unit"><strong>Review unit</strong></label>
              <Select
                id="accessibility-review-unit"
                aria-label="Review unit"
                value={reviewUnit?.id}
                options={reviewUnits.map(unit => ({ label: unit.title, value: unit.id }))}
                onChange={onUnitChange}
                style={{ marginTop: 6, width: '100%' }}
              />
              {reviewUnit && <div style={{ marginTop: 6 }}><small>{reviewUnit.rationale}</small></div>}
            </div>
            <div>
              <label htmlFor="accessibility-review-page"><strong>Target</strong></label>
              <Select
                id="accessibility-review-page"
                aria-label="Review target"
                value={selectedUrl?.normalizedUrl}
                options={targetUrls.map(url => ({
                  label: targetOptionLabel(url),
                  value: url.normalizedUrl,
                }))}
                onChange={setPageUrl}
                style={{ marginTop: 6, width: '100%' }}
              />
              {selectedUrl && (
                <div style={{ marginTop: 6 }}>
                  <small>{`Selected ${pageTypeLabel(selectedUrl.pageType)} target`}</small>
                </div>
              )}
            </div>
            <div>
              <label htmlFor="accessibility-review-outcome"><strong>Outcome</strong></label>
              <Select
                id="accessibility-review-outcome"
                aria-label="Outcome"
                value={status}
                options={[
                  { label: 'Pass', value: 'manual_pass' },
                  { label: 'Fail', value: 'manual_fail' },
                  { label: 'Not applicable', value: 'not_applicable' },
                  { label: 'Blocked', value: 'blocked' },
                ]}
                onChange={setStatus}
                style={{ marginTop: 6, width: '100%' }}
              />
            </div>
            <div>
              <label htmlFor="accessibility-review-note"><strong>Review note</strong></label>
              <Input.TextArea
                id="accessibility-review-note"
                value={rationale}
                onChange={event => setRationale(event.target.value)}
                autoSize={{ minRows: 3, maxRows: 8 }}
                placeholder="What did you check, and what supports this outcome?"
                style={{ marginTop: 6 }}
              />
            </div>
          </>
          )}
        </Space>
      )}
    </Modal>
  );
};

export const ReviewUnitModal = ({ reviewUnit, onClose, onReviewControl, onUnitUpdated }) => {
  const { result, refetch, setSelectedOccurrence } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const [updateReviewUnitStatus, { loading: updatingReviewUnitStatus }] = useMutation(UPDATE_ACCESSIBILITY_REVIEW_UNIT_STATUS, {
    onCompleted: ({ updateDataSourceAccessibilityReviewUnitStatus: updatedReviewUnit }) => {
      onUnitUpdated?.(current => (current?.id === updatedReviewUnit?.id
        ? { ...current, ...updatedReviewUnit }
        : current));
      refetch();
    },
  });
  const reviewed = new Map((reviewUnit?.manualControls || []).map(control => [control.criterionId, control]));
  const representative = representativeUrl(reviewUnit);
  const representativePageUrl = representative?.normalizedUrl;
  const targetPageType = representativePageType(reviewUnit);
  const automatedIssues = useMemo(() => accessibilityFindingGroups(result)
    .flatMap(group => group.occurrences
      .filter(occurrence => comparableUrl(occurrence.pageUrl) === comparableUrl(representativePageUrl))
      .map(occurrence => ({
        ...occurrence,
        criterionIds: group.criterionIds,
        classification: group.classification,
        status: occurrence.status || group.status,
        ruleId: group.ruleId,
        suiteType: 'Accessibility',
      }))), [result, representativePageUrl]);
  const controls = useMemo(
    () => controlsForPageType(result?.controls || [], targetPageType),
    [result?.controls, targetPageType],
  );
  const columns = [{
    title: 'WCAG criterion',
    key: 'criterion',
    render: (_, control) => (
      <div>
        <div>{`${control.id} ${control.title}`}</div>
        <WcagCriterionHelp compact criterionIds={[control.id]} />
      </div>
    ),
  }, {
    title: 'Scanner evidence',
    key: 'automatedIssues',
    render: (_, control) => {
      const issues = automatedIssues.filter(issue => issue.criterionIds.includes(control.id));
      return issues.length
        ? <Button size="small" onClick={() => { onClose(); setSelectedOccurrence(issues[0]); }}>{`View evidence (${issues.length})`}</Button>
        : '—';
    },
  }, {
    title: 'Saved outcome',
    key: 'outcome',
    render: (_, control) => {
      const outcome = reviewed.get(control.id);
      return outcome ? controlStatus(outcome.status) : 'Not reviewed';
    },
  }, {
    title: 'Last reviewed',
    key: 'reviewedAt',
    render: (_, control) => reviewed.get(control.id)?.observedAt || '—',
    type: 'datetime',
  }, {
    title: '',
    key: 'review',
    render: (_, control) => <Button size="small" onClick={() => onReviewControl(control, reviewUnit)}>Review</Button>,
  }];
  const setWorkStatus = workStatus => updateReviewUnitStatus({
    variables: { dataSourceId: dataSource.id, reviewUnitId: reviewUnit.id, workStatus },
  });

  return (
    <Modal
      title={reviewUnit ? `Manual review: ${reviewUnit.title}` : ''}
      open={Boolean(reviewUnit)}
      onCancel={onClose}
      footer={null}
      fullWidth={980}
      rootClassName={REVIEW_CLASS}
    >
      {reviewUnit && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <DetailFields>
            <DetailField label="Representative target">
              {representative ? (
                <span>
                  {`${pageTypeLabel(representative.pageType)} — `}
                  <ExternalUrl href={representative.url} />
                </span>
              ) : '—'}
            </DetailField>
            <DetailField label="Progress">{`${reviewUnit.reviewedControlCount} / ${reviewUnit.controlCount} checks`}</DetailField>
            <DetailField label="Status">{reviewUnitStatus(reviewUnit.workStatus)}</DetailField>
          </DetailFields>
          <Space wrap>
            <Button loading={updatingReviewUnitStatus} onClick={() => setWorkStatus('in_progress')}>Mark in progress</Button>
            <Button loading={updatingReviewUnitStatus} onClick={() => setWorkStatus('blocked')}>Mark blocked</Button>
            <Button
              type="primary"
              disabled={reviewUnit.reviewedControlCount !== reviewUnit.controlCount}
              loading={updatingReviewUnitStatus}
              onClick={() => setWorkStatus('completed')}
            >
              Mark complete
            </Button>
          </Space>
          <PageIntroduction title="Known scanner evidence on this representative target">
            {automatedIssues.length
              ? `${automatedIssues.length} scanner item${automatedIssues.length === 1 ? '' : 's'} ${automatedIssues.length === 1 ? 'is' : 'are'} shown alongside the related WCAG controls below. Open the evidence to inspect each automated issue or review signal.`
              : 'No scanner evidence is currently associated with this representative target.'}
          </PageIntroduction>
          <PageIntroduction title="Controls for this representative target">
            Record the relevant outcome for each applicable criterion. “Not applicable” is an outcome; it makes the coverage decision visible instead of silently skipping a check.
          </PageIntroduction>
          <Table columns={columns} dataSource={controls} pagination={{ pageSize: 10 }} resizable={false} interactive={false} scroll={TABLE_SCROLL} />
        </Space>
      )}
    </Modal>
  );
};

const ManualReview = () => {
  const { refetch, result, setSelectedOccurrence, scope, pageType, setPageType, shellLoading, loading: resultLoading } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const [acceptCoverage, { loading: acceptingCoverage }] = useMutation(ACCEPT_ACCESSIBILITY_REVIEW_COVERAGE, { onCompleted: () => refetch() });
  const [selectedReviewUnit, setSelectedReviewUnit] = useState(null);
  const [manualReview, setManualReview] = useState({ control: null, unit: null });
  const units = result?.reviewUnits?.filter(({ status }) => status === 'accepted') || [];
  const suggestedUnits = result?.reviewUnits?.filter(({ status }) => status === 'suggested') || [];
  const showPageTypeFilter = reviewHasBothPageTypes(units);
  const effectivePageType = effectiveManualReviewPageType(showPageTypeFilter, pageType);
  const visibleUnits = filterReviewUnits(units, effectivePageType);
  // Hidden All/HTML/PDF control must not keep a shared Findings/Pages selection
  // (URL + reviewResult(pageType) would otherwise filter progress/outcomes).
  // Wait until reviewUnits have loaded so a mixed-type review is not cleared during fetch.
  useEffect(() => {
    if (!Array.isArray(result?.reviewUnits)) return undefined;
    if (pageType === effectivePageType) return undefined;
    setPageType(effectivePageType);
    return undefined;
  }, [effectivePageType, pageType, result?.reviewUnits, setPageType]);
  const summary = reviewUnitSummary(visibleUnits);
  const nextUnit = visibleUnits.find(({ workStatus }) => workStatus === 'in_progress')
    || visibleUnits.find(({ workStatus }) => workStatus === 'not_started')
    || visibleUnits.find(({ workStatus }) => workStatus === 'blocked');
  const automatedIssuesByUnit = useMemo(() => new Map(visibleUnits.map(unit => {
    const representative = representativeUrl(unit);
    const issues = accessibilityFindingGroups(result).flatMap(group => group.occurrences
      .filter(occurrence => comparableUrl(occurrence.pageUrl) === comparableUrl(representative?.normalizedUrl))
      .map(occurrence => ({
        ...occurrence,
        criterionIds: group.criterionIds,
        classification: group.classification,
        status: occurrence.status || group.status,
        ruleId: group.ruleId,
        suiteType: 'Accessibility',
      })));
    return [unit.id, issues];
  })), [result, visibleUnits]);
  const columns = [{
    title: 'Review unit',
    dataIndex: 'title',
    width: 160,
    render: title => <span style={reviewUnitCellStyle} title={title}>{title}</span>,
  }, {
    title: 'Type',
    key: 'pageType',
    width: 72,
    render: (_, unit) => pageTypeLabel(representativePageType(unit)),
  }, {
    title: 'Representative target',
    key: 'representative',
    width: 220,
    render: (_, unit) => {
      const url = representativeUrl(unit)?.url;
      return url ? (
        <div style={representativeTargetStyle} title={url}>
          <ExternalUrl href={url} />
        </div>
      ) : '—';
    },
  }, {
    title: 'Scanner evidence',
    key: 'automatedIssues',
    width: 130,
    render: (_, unit) => {
      const issues = automatedIssuesByUnit.get(unit.id) || [];
      return issues.length
        ? <Button size="small" onClick={() => setSelectedOccurrence(issues[0])}>{`View evidence (${issues.length})`}</Button>
        : '—';
    },
  }, {
    title: 'Progress',
    key: 'progress',
    width: 120,
    render: (_, unit) => `${unit.reviewedControlCount} / ${unit.controlCount} checks`,
  }, {
    title: 'Status',
    dataIndex: 'workStatus',
    width: 110,
    render: reviewUnitStatus,
  }, {
    title: '',
    key: 'open',
    width: 72,
    render: (_, unit) => <Button onClick={() => setSelectedReviewUnit(unit)}>{unit.workStatus === 'not_started' ? 'Start' : 'Open'}</Button>,
  }];

  return (
    <ReviewPage title="Manual review">
      <Page.Content>
        {!shellLoading && !scope ? <InactiveNotice /> : resultLoading ? (
          <Table columns={columns} dataSource={[]} loading pagination={false} resizable={false} interactive={false} scroll={MANUAL_REVIEW_TABLE_SCROLL} tableLayout="fixed" />
        ) : !units.length ? (
          <>
            <PageIntroduction title="Set review coverage first">
              Extellio has proposed representative pages from your URL structure. Accept the proposal to create a resumable manual review queue.
            </PageIntroduction>
            <Button type="primary" loading={acceptingCoverage} onClick={() => acceptCoverage({ variables: { dataSourceId: dataSource.id } })}>Use suggested coverage</Button>
          </>
        ) : (
          <>
            <PageIntroduction title="Work through representative targets">
              Review one unit at a time. Your saved control outcomes and unit status make it possible to stop and continue later.
            </PageIntroduction>
            {suggestedUnits.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  {`${suggestedUnits.length} current HTML or PDF review targets are not yet included in the accepted coverage.`}
                </div>
                <Button type="primary" loading={acceptingCoverage} onClick={() => acceptCoverage({ variables: { dataSourceId: dataSource.id } })}>Update review coverage</Button>
              </div>
            )}
            {showPageTypeFilter && <PageTypeFilter />}
            <DetailFields style={{ marginBottom: 16 }}>
              <DetailField label="Completed">{summary.completed}</DetailField>
              <DetailField label="In progress">{summary.inProgress}</DetailField>
              <DetailField label="Blocked">{summary.blocked}</DetailField>
              <DetailField label="Remaining">{summary.remaining}</DetailField>
            </DetailFields>
            {nextUnit && <Button type="primary" onClick={() => setSelectedReviewUnit(nextUnit)} style={{ marginBottom: 16 }}>Continue review</Button>}
            <Table columns={columns} dataSource={visibleUnits} pagination={false} resizable={false} interactive={false} scroll={MANUAL_REVIEW_TABLE_SCROLL} tableLayout="fixed" />
          </>
        )}
      </Page.Content>
      <ReviewUnitModal
        reviewUnit={selectedReviewUnit}
        onClose={() => setSelectedReviewUnit(null)}
        onReviewControl={(control, unit) => setManualReview({ control, unit })}
        onUnitUpdated={setSelectedReviewUnit}
      />
      <ManualReviewModal
        control={manualReview.control}
        reviewUnit={manualReview.unit}
        onClose={() => setManualReview({ control: null, unit: null })}
        onSaved={(outcome) => {
          setSelectedReviewUnit(current => {
            if (!outcome || current?.id !== manualReview.unit?.id) return current;
            const controls = new Map((current.manualControls || []).map(saved => [saved.criterionId, saved]));
            controls.set(outcome.criterionId, outcome);
            return { ...current, manualControls: [...controls.values()], reviewedControlCount: controls.size };
          });
        }}
      />
    </ReviewPage>
  );
};

export default ManualReview;
